import React, { useState, useMemo } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  TrendingUp, TrendingDown, Search, ShoppingCart, ChevronDown,
  Handshake, UserPlus, Trophy, SkipForward
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import InfoTip from '@/components/InfoTip';
import DateRangeSelect from '@/components/DateRangeSelect';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';
import QuickAssignModal from '@/components/QuickAssignModal';

/* Trend data with proper timeline */
function genTrend90(base, pct) {
  const today = new Date();
  return Array.from({ length: 90 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    return {
      date: `${d.getMonth() + 1}/${d.getDate()}`,
      price: +(base + (base * pct / 100) * (i / 90) + (Math.random() - 0.5) * base * 0.02).toFixed(3),
    };
  });
}

/* Action dropdown */
const QuickActions = ({ mpn, onAssign }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1" data-testid={`action-${mpn}`}>
        Action <ChevronDown className="h-3 w-3" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuItem onClick={() => toast.success('Referred to 1Source')}>
        <Handshake className="h-3.5 w-3.5 mr-2 text-green-600" /> Refer to 1Source
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => onAssign?.()}>
        <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-600" /> Assign
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Report Win opened')}>
        <Trophy className="h-3.5 w-3.5 mr-2 text-amber-600" /> Report Win
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => toast.info('Skipped')}>
        <SkipForward className="h-3.5 w-3.5 mr-2 text-gray-500" /> Skip
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

/* All parts with > 10% change */
const ALL_PARTS = [
  { mpn: 'MT48LC16M16A2', category: 'Memory IC', bomRef: 'Main Controller Board v2.1', current: 3.20, predicted: 3.90, change: 22, impact: 17500, confidence: 85, reason: 'Global DRAM shortage — Taiwan fab utilization at 97%', data: genTrend90(3.20, 22) },
  { mpn: 'STM32F407VGT6', category: 'MCU', bomRef: 'Main Controller Board v2.1', current: 12.50, predicted: 14.38, change: 15, impact: 47000, confidence: 92, reason: 'Automotive demand surge across ST Micro product lines', data: genTrend90(12.50, 15) },
  { mpn: 'NRF52840-QIAA-R', category: 'MCU', bomRef: 'BLE Module v3.0', current: 5.75, predicted: 6.67, change: 16, impact: 7360, confidence: 78, reason: 'Nordic Semiconductor lead time extended to 20 weeks', data: genTrend90(5.75, 16) },
  { mpn: 'USB-C-16P-SMD', category: 'Connector', bomRef: 'IoT Sensor Board v1.0', current: 0.38, predicted: 0.44, change: 15, impact: 3000, confidence: 70, reason: 'USB-C adoption surge for automotive and IoT applications', data: genTrend90(0.38, 15) },
  { mpn: 'ESP32-WROOM-32E', category: 'Wireless IC', bomRef: 'IoT Sensor Board v1.0', current: 2.80, predicted: 2.32, change: -17, impact: 10560, confidence: 80, reason: 'New competitors (BK7231, WB2) driving prices down', data: genTrend90(2.80, -17) },
  { mpn: 'GRM188R71H104KA93D', category: 'Capacitor', bomRef: 'IoT Sensor Board v1.0', current: 0.025, predicted: 0.020, change: -20, impact: 5000, confidence: 82, reason: 'MLCC oversupply — new Murata and Samsung fabs online in Q2', data: genTrend90(0.025, -20) },
  { mpn: 'TPS54331DR', category: 'Power IC', bomRef: 'Main Controller Board v2.1', current: 1.65, predicted: 1.40, change: -15, impact: 6250, confidence: 74, reason: 'TI expanding broad-market power IC inventory', data: genTrend90(1.65, -15) },
  { mpn: 'SN65HVD230DR', category: 'CAN Transceiver', bomRef: 'CAN Bus Module v2.0', current: 0.95, predicted: 0.80, change: -16, impact: 1800, confidence: 71, reason: 'Commodity CAN transceivers — multiple new entrants', data: genTrend90(0.95, -16) },
];

const BuyTimingPage = () => {
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});
  const { format } = useCurrency();
  const [searchQuery, setSearchQuery] = useState('');
  const [minImpact, setMinImpact] = useState('');
  const [minPct, setMinPct] = useState('');
  const [dateRange, setDateRange] = useState('90d');

  const filtered = useMemo(() => {
    return ALL_PARTS.filter(p => {
      const q = searchQuery.toLowerCase();
      if (q && !p.mpn.toLowerCase().includes(q) && !p.category.toLowerCase().includes(q)) return false;
      if (minImpact && p.impact < Number(minImpact)) return false;
      if (minPct && Math.abs(p.change) < Number(minPct)) return false;
      return true;
    });
  }, [searchQuery, minImpact, minPct]);

  const buyNow = filtered.filter(p => p.change > 0).sort((a, b) => b.impact - a.impact);
  const holdItems = filtered.filter(p => p.change < 0).sort((a, b) => b.impact - a.impact);

  const totalBuyImpact = buyNow.reduce((s, i) => s + i.impact, 0);
  const totalHoldImpact = holdItems.reduce((s, i) => s + i.impact, 0);

  const renderCard = (item, isBuy) => (
    <div key={item.mpn} className="p-4 border border-gray-200 rounded-lg bg-white" data-testid={`${isBuy ? 'buy' : 'hold'}-${item.mpn}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono font-bold text-gray-900">{item.mpn}</span>
            <Badge variant="outline" className="text-xs">{item.category}</Badge>
            <Badge variant="outline" className="text-[10px] text-gray-500">{item.bomRef}</Badge>
            <Badge className="bg-blue-100 text-blue-800 text-xs">{item.confidence}% confidence</Badge>
          </div>
          <p className="text-sm text-gray-600 mb-3">{item.reason}</p>
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Current</p>
              <p className="font-semibold">{format(item.current)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Predicted (3m)</p>
              <p className={`font-bold ${isBuy ? 'text-red-600' : 'text-green-600'}`}>{format(item.predicted)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Price Change</p>
              <p className={`font-bold ${isBuy ? 'text-red-600' : 'text-green-600'}`}>{item.change > 0 ? '+' : ''}{item.change}%</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Value Impact</p>
              <p className="font-bold text-gray-900">{format(item.impact)}</p>
            </div>
          </div>
        </div>
        {/* Improved Chart */}
        <div className="w-64 h-28 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={item.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={Math.floor(item.data.length / 5)} />
              <YAxis domain={['auto', 'auto']} tick={{ fontSize: 9 }} width={40} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={v => [`$${Number(v).toFixed(3)}`, 'Price']} labelFormatter={l => `Date: ${l}`} />
              <Area type="monotone" dataKey="price" stroke={isBuy ? '#ef4444' : '#16a34a'} fill={isBuy ? '#fee2e2' : '#dcfce7'} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" className={isBuy ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'} data-testid={`buy-now-btn-${item.mpn}`}
          onClick={() => {
            if (isBuy) {
              toast.success(`Buy Now order initiated for ${item.mpn}`);
            } else {
              toast.success(`Price alert set for ${item.mpn} — you'll be notified when price drops`);
            }
          }}>
          <ShoppingCart className="h-3.5 w-3.5 mr-1" /> {isBuy ? 'Buy Now' : 'Set Price Alert'}
        </Button>
        <QuickActions mpn={item.mpn} />
      </div>
    </div>
  );

  return (
    <Layout title="Buy Now / Hold" subtitle="AI-predicted price movements — only parts with >10% change in next 3 months">
      <div className="space-y-6" data-testid="buy-timing-page">
        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-red-50 border-red-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-red-600 uppercase flex items-center"><TrendingUp className="h-3 w-3 mr-1" /> Buy Now Impact <InfoTip text="Total cost impact if prices rise as predicted. Buy now to avoid overpaying." /></p>
              <p className="text-2xl font-bold text-red-700">{format(totalBuyImpact)}</p>
              <p className="text-xs text-red-500 mt-1">{buyNow.length} components rising</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-green-600 uppercase flex items-center"><TrendingDown className="h-3 w-3 mr-1" /> Hold Savings <InfoTip text="Potential savings by waiting for predicted price drops." /></p>
              <p className="text-2xl font-bold text-green-700">{format(totalHoldImpact)}</p>
              <p className="text-xs text-green-500 mt-1">{holdItems.length} components declining</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-5">
              <p className="text-xs text-gray-500 uppercase">Total Parts Flagged</p>
              <p className="text-2xl font-bold text-gray-900">{filtered.length}</p>
              <p className="text-xs text-gray-400 mt-1">Only &gt;10% price movement shown</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex gap-3 items-center flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input placeholder="Search MPN or category..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="buy-search" />
              </div>
              <Input type="number" placeholder="Min value impact ($)" value={minImpact} onChange={e => setMinImpact(e.target.value)} className="w-44 h-9" data-testid="min-impact-filter" />
              <Input type="number" placeholder="Min % change" value={minPct} onChange={e => setMinPct(e.target.value)} className="w-36 h-9" data-testid="min-pct-filter" />
              <DateRangeSelect value={dateRange} onChange={setDateRange} testId="buy-date-range" />
            </div>
          </CardContent>
        </Card>

        {/* Buy Now */}
        {buyNow.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-red-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <TrendingUp className="h-5 w-5" />
                Buy Now — Price Increases Expected
                <Badge className="bg-red-100 text-red-800 ml-2">{buyNow.length} items</Badge>
                <InfoTip text="Components with predicted price increases >10% in the next 3 months. Buy now to lock in current rates." />
              </CardTitle>
            </CardHeader>
            <CardContent><div className="space-y-4">{buyNow.map(item => renderCard(item, true))}</div></CardContent>
          </Card>
        )}

        {/* Hold */}
        {holdItems.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-green-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <TrendingDown className="h-5 w-5" />
                Hold — Price Decreases Expected
                <Badge className="bg-green-100 text-green-800 ml-2">{holdItems.length} items</Badge>
                <InfoTip text="Components with predicted price declines >10% in the next 3 months. Consider waiting for better rates." />
              </CardTitle>
            </CardHeader>
            <CardContent><div className="space-y-4">{holdItems.map(item => renderCard(item, false))}</div></CardContent>
          </Card>
        )}

        {filtered.length === 0 && (
          <Card className="bg-gray-50 border-gray-200"><CardContent className="pt-8 pb-8 text-center text-gray-500">No components match your filters.</CardContent></Card>
        )}
      </div>
      <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />
    </Layout>
  );
};

export default BuyTimingPage;
